import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { routes } from "../../../../store/routes";
import { trackingReportsService } from "../../../../store/services/ServiceTrackingReports";
import { Icon, Divider, Grid, Form, Segment, Table, Header, Menu } from "semantic-ui-react";
import HeaderTitle from "../../../../components/others/HeaderTitle";
import "chart.js/auto";
import { Line, Bar } from "react-chartjs-2";
import RegionMap from "./RegionMap";
import HourDayHeatmap from "./HourDayHeatmap";
import moment from "moment";

const MAP_METRICS = [
    { key: "search_count", label: "Dopyt (vyhľadávania)", short: "Dopyt", color: "#d60c8c", btn: "pink" },
    { key: "supply_count", label: "Ponuka (aktivity)", short: "Ponuka", color: "#2185d0", btn: "blue" },
    { key: "no_results_count", label: "Vyhľadávanie bez výsledkov", short: "Vyhľadávanie bez výsledkov", color: "#f2711c", btn: "orange" },
    { key: "demand_supply_ratio", label: "Dopyt / Ponuka", short: "Dopyt/Ponuka", color: "#6435c9", btn: "purple" },
];

const RANGE_OPTIONS = [
    { key: 7, value: 7, text: "Posledných 7 dní" },
    { key: 30, value: 30, text: "Posledných 30 dní" },
    { key: 90, value: 90, text: "Posledných 90 dní" },
];

const MODE_LABEL = { business: "#APbusiness", solidarity: "#APsolidarity" };

const EmptyState = ({ icon = "chart bar outline", text = "Žiadne dáta", hint = "Zatiaľ tu nie sú žiadne záznamy.", padding = "1.6rem 0" }) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.45rem", padding, textAlign: "center" }}>
        <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={icon} style={{ margin: 0, fontSize: "1.2rem", color: "#c2c6cc" }} />
        </div>
        <span style={{ fontSize: "0.88rem", fontWeight: "bold", color: "#9aa0a6" }}>{text}</span>
        {hint && <span style={{ fontSize: "0.85rem", color: "#6b7280", lineHeight: 1.45 }}>{hint}</span>}
    </div>
);

const C = {
    pink: "#d60c8c",
    pinkSoft: "rgba(214, 12, 140, 0.12)",
    blue: "#2185d0",
    blueSoft: "rgba(33, 133, 208, 0.12)",
    green: "#21ba45",
    greenSoft: "rgba(33, 186, 69, 0.12)",
    orange: "#f2711c",
    purple: "#6435c9",
    grey: "#9aa0a6",
};

const richTooltip = {
    enabled: true,
    backgroundColor: "rgba(33, 37, 41, 0.95)",
    titleColor: "#fff",
    bodyColor: "#fff",
    padding: 12,
    cornerRadius: 8,
    displayColors: true,
    usePointStyle: true,
    titleFont: { size: 14, weight: "bold" },
    bodyFont: { size: 13 },
    bodySpacing: 6,
    boxPadding: 4,
};

const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: richTooltip,
    },
};

const lineOptions = {
    ...baseOptions,
    interaction: { mode: "index", intersect: false },
    hover: { mode: "index", intersect: false },
    plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: { ...richTooltip, mode: "index", intersect: false },
    },
    scales: { y: { beginAtZero: true } },
};

const sectionStyle = { borderRadius: "8px", border: "1px solid #eee", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };
const hRow = { display: "flex", alignItems: "center", gap: "0.5rem" };
const sectionHint = { color: "#4a4f57", fontSize: "0.9rem", lineHeight: 1.5 };
const legendHint = { fontSize: "0.85rem", color: "#5c6370" };
const SLOT_GRAY = "rgba(210, 214, 220, 0.55)";
const HOUR_MIN_SLOTS = 4;

function buildDateRange(from, to) {
    const dates = [];
    const cursor = moment(from);
    const end = moment(to);
    while (cursor.isSameOrBefore(end, "day")) {
        dates.push(cursor.format("YYYY-MM-DD"));
        cursor.add(1, "day");
    }
    return dates;
}

function buildHourRange(byHour) {
    const hoursWithData = byHour.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0);
    if (hoursWithData.length === 0) {
        return [9, 10, 11, 12];
    }
    if (hoursWithData.length < HOUR_MIN_SLOTS) {
        let start = Math.min(...hoursWithData);
        let end = Math.max(...hoursWithData);
        while (end - start + 1 < HOUR_MIN_SLOTS) {
            if (start > 0) start -= 1;
            else if (end < 23) end += 1;
            else break;
        }
        return Array.from({ length: end - start + 1 }, (_, k) => start + k);
    }
    return hoursWithData;
}

function chartPointStyle(value, activeColor) {
    if (value == null) {
        return { radius: 3, bg: "#e5e7eb", border: "#d1d5db" };
    }
    return { radius: 2, bg: activeColor, border: activeColor };
}

function Analytics() {
    const user = useSelector((state) => state.user);
    const [days, setDays] = useState(30);
    const [isLoading, setIsLoading] = useState(false);
    const [overview, setOverview] = useState(null);
    const [activities, setActivities] = useState([]);
    const [demandGeo, setDemandGeo] = useState({ regions: [], localities: [] });
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [mapMetric, setMapMetric] = useState("search_count");
    const [panelTab, setPanelTab] = useState("stats");
    const [showAllCities, setShowAllCities] = useState(false);
    const [showAllActivities, setShowAllActivities] = useState(false);

    const dateRange = useCallback(() => {
        const to = moment().format("YYYY-MM-DD");
        const from = moment().subtract(days - 1, "days").format("YYYY-MM-DD");
        return { from, to };
    }, [days]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const { from, to } = dateRange();
        const params = `?from=${from}&to=${to}`;
        const [overviewRes, activitiesRes, demandGeoRes] = await Promise.all([
            trackingReportsService.getOverview(params),
            trackingReportsService.getActivities(params),
            trackingReportsService.getDemandGeo(params),
        ]);
        if (overviewRes?.status === 200) setOverview(overviewRes.response);
        if (activitiesRes?.status === 200) setActivities(activitiesRes.response.activities || []);
        setShowAllActivities(false);
        if (demandGeoRes?.status === 200) {
            setDemandGeo({
                regions: demandGeoRes.response.regions || [],
                localities: demandGeoRes.response.localities || [],
            });
        }
        setIsLoading(false);
    }, [dateRange]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        setShowAllCities(false);
    }, [selectedRegion, panelTab]);

    const funnel = overview?.funnel || { l1: 0, l2: 0, l3: 0, l4: 0, l5: 0 };
    const totals = overview?.totals || {};
    const prevTotals = overview?.prev_totals || {};
    const daily = overview?.daily || [];
    const byType = overview?.by_type || { business: { views: 0, cart_adds: 0 }, solidarity: { views: 0, cart_adds: 0 } };
    const rate = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
    const delta = (cur, prev) => (prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null);

    const totalSearches = (totals.search || 0) + (totals.search_no_results || 0);
    const prevSearches = (prevTotals.search || 0) + (prevTotals.search_no_results || 0);
    const noResultsRate = rate(totals.search_no_results || 0, totalSearches);

    const kpis = [
        { icon: "search", color: C.pink, value: totalSearches, label: "Vyhľadávaní", delta: delta(totalSearches, prevSearches) },
        { icon: "eye", color: C.blue, value: totals.detail_view || 0, label: "Zobrazenie aktivít", delta: delta(totals.detail_view || 0, prevTotals.detail_view || 0) },
        { icon: "shopping cart", color: C.green, value: totals.cart_add || 0, label: "Pridaní do košíka", delta: delta(totals.cart_add || 0, prevTotals.cart_add || 0) },
        { icon: "warning circle", color: C.orange, value: `${noResultsRate}%`, label: "Vyhľadávaní bez výsledkov", delta: null },
    ];

    const cartToPaid = rate(funnel.l5 || 0, funnel.l4 || 0);
    const cartAbandon = funnel.l4 > 0 ? Math.max(0, 100 - cartToPaid) : 0;

    const funnelSteps = [
        { key: "l1", label: "Vyhľadávanie", icon: "search", value: funnel.l1, prev: funnel.l1 },
        { key: "l2", label: "Prejavený záujem", icon: "mouse pointer", value: funnel.l2, prev: funnel.l1 },
        { key: "l3", label: "Zobrazenie detailu", icon: "eye", value: funnel.l3, prev: funnel.l2 },
        { key: "l4", label: "Pridanie do košíka", icon: "shopping cart", value: funnel.l4, prev: funnel.l3 },
        { key: "l5", label: "Zaplatené", icon: "credit card outline", value: funnel.l5 || 0, prev: funnel.l4 },
    ];

    const { from: rangeFrom, to: rangeTo } = dateRange();
    const dailyByDate = daily.reduce((acc, d) => {
        acc[d.date] = d;
        return acc;
    }, {});
    const timelineDates = buildDateRange(rangeFrom, rangeTo);
    const timelineDaily = timelineDates.map((date) => {
        const row = dailyByDate[date];
        return {
            date,
            search: row ? row.search : null,
            detail_view: row ? row.detail_view : null,
            cart_add: row ? row.cart_add : null,
        };
    });
    const dailyChartLabels = timelineDaily.map((d) => moment(d.date).format("DD.MM"));
    const dailyLineDataset = (key, borderColor, backgroundColor) => ({
        label: key === "search" ? "Vyhľadávania" : key === "detail_view" ? "Zobrazenia detailov" : "Do košíka",
        data: timelineDaily.map((d) => d[key]),
        borderColor,
        backgroundColor,
        fill: true,
        tension: 0.35,
        spanGaps: false,
        borderWidth: 2,
        pointRadius: (ctx) => chartPointStyle(ctx.dataset.data[ctx.dataIndex], borderColor).radius,
        pointHoverRadius: 6,
        pointBackgroundColor: (ctx) => chartPointStyle(ctx.dataset.data[ctx.dataIndex], borderColor).bg,
        pointBorderColor: (ctx) => chartPointStyle(ctx.dataset.data[ctx.dataIndex], borderColor).border,
        pointHoverBackgroundColor: borderColor,
    });
    const dailyData = {
        labels: dailyChartLabels,
        datasets: [
            dailyLineDataset("search", C.pink, C.pinkSoft),
            dailyLineDataset("detail_view", C.blue, C.blueSoft),
            dailyLineDataset("cart_add", C.green, C.greenSoft),
        ],
    };
    const dailyLineOptions = {
        ...lineOptions,
        plugins: {
            ...lineOptions.plugins,
            tooltip: {
                ...richTooltip,
                filter: (item) => item.parsed.y != null,
            },
        },
    };

    const byTypeRows = [
        { label: "Prejavený záujem", biz: byType.business?.interest || 0, sol: byType.solidarity?.interest || 0 },
        { label: "Zobrazenia detailov", biz: byType.business?.views || 0, sol: byType.solidarity?.views || 0 },
        { label: "Do košíka", biz: byType.business?.cart_adds || 0, sol: byType.solidarity?.cart_adds || 0 },
    ];
    const byTypeTotal = byTypeRows.reduce((s, r) => s + r.biz + r.sol, 0);

    const byActivityType = overview?.by_activity_type || [];
    const noFilter = overview?.no_filter || { search_count: 0, no_results_count: 0 };
    const typeRows = [...byActivityType].sort((a, b) => (b.search_count || 0) - (a.search_count || 0));
    const maxTypeSearch = typeRows.reduce((m, t) => Math.max(m, t.search_count || 0), 0);

    const topActivities = activities.slice(0, 8);
    const maxActViews = topActivities.reduce((m, a) => Math.max(m, a.views || 0), 0);
    const maxTableViews = activities.reduce((m, a) => Math.max(m, a.views || 0), 0);

    const geoRegions = demandGeo.regions || [];
    const metricMeta = MAP_METRICS.find((m) => m.key === mapMetric) || MAP_METRICS[0];
    const mapValues = geoRegions.reduce((acc, r) => {
        const value = r[mapMetric];
        acc[r.region] = value == null ? 0 : value;
        return acc;
    }, {});
    const selectedDetail = selectedRegion ? geoRegions.find((r) => r.region === selectedRegion) : null;
    const localitiesFiltered = (demandGeo.localities || []).filter(
        (l) => !selectedRegion || l.region === selectedRegion
    );
    const detailRows = selectedDetail
        ? [
              { label: "Vyhľadávania (dopyt)", value: selectedDetail.search_count, color: C.pink },
              { label: "Vyhľadávanie bez výsledkov", value: selectedDetail.no_results_count, color: C.orange },
              { label: "Kliky na výsledok", value: selectedDetail.result_clicks, color: C.blue },
              { label: "Pridania do košíka", value: selectedDetail.cart_adds, color: C.green },
              { label: "Ponuka (počet aktivít)", value: selectedDetail.supply_count, color: C.purple },
          ]
        : [];

    const ratioInfo = (() => {
        if (!selectedDetail) return null;
        const ratio = selectedDetail.demand_supply_ratio;
        if (ratio == null) {
            return { ratio: null, text: "Žiadna ponuka v kraji", color: C.grey, soft: "#f1f1f3" };
        }
        if (ratio >= 5) {
            return { ratio, text: "Vysoký nenaplnený dopyt", color: "#db2828", soft: "rgba(219,40,40,0.12)" };
        }
        if (ratio >= 2) {
            return { ratio, text: "Dopyt prevyšuje ponuku", color: C.orange, soft: "rgba(242,113,28,0.12)" };
        }
        return { ratio, text: "Dopyt a ponuka vyvážené", color: C.green, soft: C.greenSoft };
    })();

    const CITY_METRICS = [
        { key: "search_count", label: "Dopyt", color: C.pink, soft: C.pinkSoft },
        { key: "supply_count", label: "Ponuka", color: C.blue, soft: C.blueSoft },
        { key: "no_results_count", label: "Vyhľadávanie bez výsledkov", color: C.orange, soft: "rgba(242,113,28,0.12)" },
        { key: "cart_adds", label: "Do košíka", color: C.green, soft: C.greenSoft },
    ];
    const cityMetricKey = mapMetric === "supply_count"
        ? "supply_count"
        : mapMetric === "no_results_count"
            ? "no_results_count"
            : "search_count";
    const cityFallback = mapMetric === "demand_supply_ratio";
    const cityPrimary = CITY_METRICS.find((m) => m.key === cityMetricKey) || CITY_METRICS[0];
    const cityPool = mapMetric === "supply_count"
        ? localitiesFiltered.filter((l) => (Number(l.supply_count) || 0) > 0)
        : localitiesFiltered;
    const citySorted = [...cityPool].sort(
        (a, b) => (Number(b[cityMetricKey]) || 0) - (Number(a[cityMetricKey]) || 0)
    );

    const byHour = overview?.by_hour || [];
    const byWeekday = overview?.by_weekday || [];
    const heatmap = overview?.heatmap || [];
    const peakHour = byHour.length ? byHour.indexOf(Math.max(...byHour)) : null;
    const peakWeekdayIdx = byWeekday.length ? byWeekday.indexOf(Math.max(...byWeekday)) : null;
    const weekdayShort = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
    const weekdayFull = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];

    const hourRange = buildHourRange(byHour);
    const hourVals = hourRange.map((i) => byHour[i] || 0);
    const hourValsWithData = hourVals.filter((v) => v > 0);
    const maxHourVal = hourValsWithData.length ? Math.max(...hourValsWithData) : 0;
    const minHourVal = hourValsWithData.length ? Math.min(...hourValsWithData) : 0;
    const maxWeekdayVal = byWeekday.length ? Math.max(...byWeekday) : 0;
    const minWeekdayVal = byWeekday.length ? Math.min(...byWeekday.filter((v) => v > 0), 0) : 0;
    const pinkScale = (v, min, max) => {
        if (!v) return SLOT_GRAY;
        const t = max > min ? (v - min) / (max - min) : 1;
        const eased = Math.pow(t, 0.7);
        return `rgba(214, 12, 140, ${(0.12 + 0.88 * eased).toFixed(3)})`;
    };

    const hourData = {
        labels: hourRange.map((i) => `${i}`),
        datasets: [{
            label: "Vyhľadávania",
            data: hourRange.map((i) => byHour[i] || 0),
            backgroundColor: hourRange.map((i) => pinkScale(byHour[i] || 0, minHourVal, maxHourVal)),
            borderRadius: 4,
        }],
    };
    const weekdayData = {
        labels: weekdayShort,
        datasets: [{
            label: "Vyhľadávania",
            data: byWeekday,
            backgroundColor: byWeekday.map((v) => pinkScale(v, minWeekdayVal, maxWeekdayVal)),
            borderRadius: 4,
        }],
    };
    const barVerticalOptions = {
        ...baseOptions,
        plugins: { legend: { display: false }, tooltip: richTooltip },
        scales: { y: { beginAtZero: true }, x: { grid: { display: false } } },
    };

    return (
        <>
            {user.role === "Admin" && (
                <Fragment>
                    <HeaderTitle title={"Analytika správania používateľov"} />
                    <section>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
                            <Link to={routes.DASHBOARD}>
                                <span style={{ fontSize: "1.2rem", color: "black" }}>
                                    <Icon name="arrow alternate circle left" /> Návrat na prehľad
                                </span>
                            </Link>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", flexWrap: "wrap" }}>
                                <Form>
                                    <Form.Select
                                        value={days}
                                        options={RANGE_OPTIONS}
                                        onChange={(e, { value }) => setDays(value)}
                                    />
                                </Form>
                            </div>
                        </div>

                        <Divider />

                        <Segment basic loading={isLoading} style={{ padding: 0, display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem", order: 1 }}>
                                {kpis.map((kpi) => (
                                    <div key={kpi.label} style={{
                                        flex: "1 1 200px",
                                        background: "var(--white)",
                                        borderRadius: "8px",
                                        border: "1px solid #eee",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                        padding: "1.2rem 1.4rem",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: "1rem",
                                        borderLeft: `4px solid ${kpi.color}`,
                                    }}>
                                        <div style={{ textAlign: "left", minWidth: 0 }}>
                                            <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                                                <span style={{ fontSize: "1.9rem", fontWeight: "bold", lineHeight: 1 }}>{kpi.value}</span>
                                                {kpi.delta != null && (
                                                    <span style={{ fontSize: "0.85rem", fontWeight: "bold", color: kpi.delta >= 0 ? C.green : "#db2828" }}>
                                                        <Icon name={kpi.delta >= 0 ? "caret up" : "caret down"} style={{ margin: 0 }} />
                                                        {Math.abs(kpi.delta)}%
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ color: "#888", fontSize: "0.95rem", marginTop: "0.3rem" }}>
                                                {kpi.label}
                                                {kpi.delta != null && <span style={{ color: "#6b7280", fontSize: "0.85rem" }}> vs. predošlé obdobie</span>}
                                            </div>
                                        </div>
                                        <Icon name={kpi.icon} size="big" style={{ color: kpi.color, margin: 0, flexShrink: 0 }} />
                                    </div>
                                ))}
                            </div>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 2 }}>
                                <Header as="h3" style={hRow}><Icon name="filter" style={{ color: C.pink, fontSize: "1em", margin: 0 }} /><span>Konverzný lievik</span></Header>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "0.75rem" }}>
                                    {funnelSteps.map((step, idx) => {
                                        const widthPct = funnel.l1 > 0 ? Math.max((step.value / funnel.l1) * 100, 2) : 0;
                                        const stepRate = rate(step.value, step.prev);
                                        return (
                                            <div key={step.key} style={{ display: "flex", alignItems: "center" }}>
                                                <div style={{ width: "180px", flexShrink: 0, fontWeight: "bold", color: "#555" }}>
                                                    <Icon name={step.icon} /> {step.label}
                                                </div>
                                                <div style={{ flex: 1, background: "#f1f1f3", borderRadius: "6px", height: "44px", overflow: "hidden" }}>
                                                    <div style={{
                                                        width: `${widthPct}%`,
                                                        height: "100%",
                                                        background: "linear-gradient(90deg, var(--primary), #ff5fb0)",
                                                        borderRadius: "6px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        paddingLeft: "1rem",
                                                        color: "var(--white)",
                                                        fontWeight: "bold",
                                                        fontSize: "1.15rem",
                                                        transition: "width 0.5s ease",
                                                    }}>
                                                        {step.value}
                                                    </div>
                                                </div>
                                                <div style={{ width: "150px", flexShrink: 0, textAlign: "right", paddingLeft: "1rem", ...legendHint }}>
                                                    {idx === 0 ? "vstup" : `${stepRate}% z predošlého`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Divider />
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", color: "#555" }}>
                                    <div>
                                        Vyhľadávanie → košík:{" "}
                                        <strong style={{ color: "var(--primary)", fontSize: "1.1rem" }}>{rate(funnel.l4, funnel.l1)}%</strong>
                                    </div>
                                    <div>
                                        Košík → platba:{" "}
                                        <strong style={{ color: C.green, fontSize: "1.1rem" }}>{cartToPaid}%</strong>
                                    </div>
                                    <div>
                                        Opustenie košíka:{" "}
                                        <strong style={{ color: "#db2828", fontSize: "1.1rem" }}>{cartAbandon}%</strong>
                                    </div>
                                </div>
                            </Segment>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 7 }}>
                                <Header as="h3" style={hRow}><Icon name="chart line" style={{ color: C.blue, fontSize: "1em", margin: 0 }} /><span>Denný vývoj</span></Header>
                                <div style={{ height: "320px" }}>
                                    <Line data={dailyData} options={dailyLineOptions} />
                                </div>
                            </Segment>

                            <Grid stackable columns={2} style={{ marginBottom: "0.5rem", order: 8 }}>
                                <Grid.Column>
                                    <Segment style={{ ...sectionStyle, height: "100%" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                                            <Header as="h3" style={{ ...hRow, margin: 0 }}><Icon name="clock outline" style={{ color: C.blue, fontSize: "1em", margin: 0 }} /><span>Top časy vyhľadávania</span></Header>
                                            {peakHour != null && byHour[peakHour] > 0 && (
                                                <span style={{ background: C.pinkSoft, color: C.pink, borderRadius: 12, padding: "2px 12px", fontWeight: "bold", fontSize: "0.85rem" }}>
                                                    Špička: {peakHour}:00–{peakHour + 1}:00
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ ...sectionHint, margin: "0.35rem 0 0.85rem" }}>Koľko vyhľadávaní pribudlo v jednotlivých hodinách</div>
                                        <div style={{ height: "280px" }}>
                                            <Bar data={hourData} options={barVerticalOptions} />
                                        </div>
                                    </Segment>
                                </Grid.Column>
                                <Grid.Column>
                                    <Segment style={{ ...sectionStyle, height: "100%" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                                            <Header as="h3" style={{ ...hRow, margin: 0 }}><Icon name="calendar alternate outline" style={{ color: C.purple, fontSize: "1em", margin: 0 }} /><span>Top deň v týždni</span></Header>
                                            {peakWeekdayIdx != null && byWeekday[peakWeekdayIdx] > 0 && (
                                                <span style={{ background: C.pinkSoft, color: C.pink, borderRadius: 12, padding: "2px 12px", fontWeight: "bold", fontSize: "0.85rem" }}>
                                                    Najsilnejší: {weekdayFull[peakWeekdayIdx]}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ ...sectionHint, margin: "0.35rem 0 0.85rem" }}>Koľko vyhľadávaní pribudlo v jednotlivých dňoch</div>
                                        <div style={{ height: "280px" }}>
                                            <Bar data={weekdayData} options={barVerticalOptions} />
                                        </div>
                                    </Segment>
                                </Grid.Column>
                            </Grid>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 9 }}>
                                <Header as="h3" style={hRow}><Icon name="th" style={{ color: C.pink, fontSize: "1em", margin: 0 }} /><span>Heatmapa: hodina × deň</span></Header>
                                <div style={{ ...sectionHint, margin: "0.35rem 0 0.9rem" }}>Tmavšie polia = viac vyhľadávaní v daný deň a hodinu</div>
                                <HourDayHeatmap matrix={heatmap} />
                            </Segment>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 6 }}>
                                <Header as="h3" style={hRow}><Icon name="trophy" style={{ color: C.orange, fontSize: "1em", margin: 0 }} /><span>Najsledovanejšie aktivity</span></Header>
                                <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: "8px", padding: "0.9rem 1.1rem", margin: "0.6rem 0 1.2rem" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.3rem" }}>
                                        <span style={{ fontWeight: "bold", color: "#555" }}>Rozdelenie podľa typu ponuky</span>
                                        <div style={{ display: "flex", gap: "1rem", ...legendHint }}>
                                            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.pink, marginRight: 5 }} />#APbusiness</span>
                                            <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.blue, marginRight: 5 }} />#APsolidarity</span>
                                        </div>
                                    </div>
                                    {byTypeTotal === 0 ? (
                                        <EmptyState icon="tags" />
                                    ) : (
                                        byTypeRows.map((row) => {
                                            const total = row.biz + row.sol;
                                            const bizPct = total ? Math.round((row.biz / total) * 100) : 0;
                                            return (
                                                <div key={row.label} style={{ marginBottom: "0.7rem" }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: "0.83rem", marginBottom: "3px" }}>
                                                        <span style={{ color: "#666", fontWeight: "bold" }}>{row.label}</span>
                                                        <span>
                                                            <span style={{ color: C.pink, fontWeight: "bold" }}>{row.biz}</span>
                                                            <span style={{ color: "#ccc" }}> / </span>
                                                            <span style={{ color: C.blue, fontWeight: "bold" }}>{row.sol}</span>
                                                            {total > 0 && <span style={{ color: "#6b7280", fontSize: "0.8rem" }}> · {bizPct}% / {100 - bizPct}%</span>}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: "flex", height: "16px", borderRadius: "5px", overflow: "hidden", background: "#eee" }}>
                                                        {row.biz > 0 && <div style={{ flex: row.biz, background: C.pink }} />}
                                                        {row.sol > 0 && <div style={{ flex: row.sol, background: C.blue }} />}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.9rem" }}>
                                    <div style={sectionHint}>Najčastejšie zobrazené detaily aktivít</div>
                                    <div style={{ display: "flex", gap: "1rem", ...legendHint }}>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.pink, marginRight: 5 }} />#APbusiness</span>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.blue, marginRight: 5 }} />#APsolidarity</span>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.green, marginRight: 5 }} />Do košíka</span>
                                    </div>
                                </div>
                                {topActivities.length === 0 ? (
                                    <EmptyState />
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
                                        {topActivities.map((a) => {
                                            const barColor = a.mode === "solidarity" ? C.blue : C.pink;
                                            const viewsPct = maxActViews > 0 ? Math.max((a.views / maxActViews) * 100, 2) : 0;
                                            const cartPct = maxActViews > 0 ? ((a.cart_adds || 0) / maxActViews) * 100 : 0;
                                            return (
                                                <div key={a.activity_id}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.25rem" }}>
                                                        <span style={{ fontWeight: "bold", color: "#444" }}>
                                                            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: barColor, marginRight: 6 }} />
                                                            {a.name}
                                                        </span>
                                                        <span style={{ color: "#888", fontSize: "0.85rem" }}>
                                                            <strong style={{ color: "#444" }}>{a.views}</strong> zobrazení
                                                            <span style={{ color: C.green, marginLeft: 8, fontWeight: "bold" }}>{a.cart_adds || 0} do košíka</span>
                                                        </span>
                                                    </div>
                                                    <div style={{ width: "100%", height: "16px", borderRadius: "5px", background: "#f1f1f3", overflow: "hidden" }}>
                                                        <div style={{ width: `${viewsPct}%`, minWidth: "2px", height: "100%", borderRadius: "5px", background: barColor }} />
                                                    </div>
                                                    <div style={{ width: "100%", height: "5px", borderRadius: "3px", background: "#f1f1f3", overflow: "hidden", marginTop: "3px" }}>
                                                        <div style={{ width: `${cartPct}%`, height: "100%", borderRadius: "3px", background: C.green }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Segment>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 5 }}>
                                <Header as="h3" style={hRow}>
                                    <Icon name="tags" style={{ color: C.pink, fontSize: "1em", margin: 0 }} />
                                    <span>Dopyt podľa typu aktivity <span style={{ color: "#6b7280", fontWeight: "normal" }}>(kategórie)</span></span>
                                </Header>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", margin: "0.35rem 0 1rem" }}>
                                    <div style={sectionHint}>Najhľadanejšie kategórie vo vyhľadávaní</div>
                                    <div style={{ display: "flex", gap: "1rem", ...legendHint }}>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.pink, marginRight: 5 }} />Nájdené</span>
                                        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: C.orange, marginRight: 5 }} />Vyhľadávanie bez výsledkov</span>
                                    </div>
                                </div>
                                {typeRows.length === 0 && noFilter.search_count === 0 ? (
                                    <EmptyState />
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                                        {noFilter.search_count > 0 && (() => {
                                            const total = noFilter.search_count || 0;
                                            const noRes = noFilter.no_results_count || 0;
                                            const found = Math.max(total - noRes, 0);
                                            const noResPct = total > 0 ? Math.round((noRes / total) * 100) : 0;
                                            return (
                                                <div style={{ paddingBottom: "0.85rem", borderBottom: "1px dashed #e5e5e5" }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.25rem" }}>
                                                        <span style={{ fontWeight: "bold", color: C.grey }}>
                                                            <Icon name="asterisk" style={{ color: C.grey }} /> Bez filtra (naprieč kategóriami)
                                                        </span>
                                                        <span style={{ color: "#888", fontSize: "0.85rem" }}>
                                                            <strong style={{ color: "#444" }}>{total}</strong> vyhľadávaní
                                                            {noRes > 0 && (
                                                                <span style={{ color: C.orange, marginLeft: 8, fontWeight: "bold" }}>{noResPct}% bez výsledkov</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div style={{ width: "100%", display: "flex", height: "22px", borderRadius: "6px", overflow: "hidden", background: "#f1f1f3" }}>
                                                        {found > 0 && <div style={{ flex: found, background: C.grey }} />}
                                                        {noRes > 0 && <div style={{ flex: noRes, background: C.orange }} />}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        {typeRows.map((t) => {
                                            const total = t.search_count || 0;
                                            const noRes = t.no_results_count || 0;
                                            const found = Math.max(total - noRes, 0);
                                            const widthPct = maxTypeSearch > 0 ? Math.max((total / maxTypeSearch) * 100, 2) : 0;
                                            const noResPct = total > 0 ? Math.round((noRes / total) * 100) : 0;
                                            return (
                                                <div key={t.name}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.25rem" }}>
                                                        <span style={{ fontWeight: "bold", color: "#444" }}>{t.name}</span>
                                                        <span style={{ color: "#888", fontSize: "0.85rem" }}>
                                                            <strong style={{ color: "#444" }}>{total}</strong> vyhľadávaní
                                                            {noRes > 0 && (
                                                                <span style={{ color: C.orange, marginLeft: 8, fontWeight: "bold" }}>{noResPct}% bez výsledkov</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div style={{ width: "100%", height: "22px", borderRadius: "6px", overflow: "hidden", background: "#f1f1f3" }}>
                                                        <div style={{ width: `${widthPct}%`, minWidth: "2px", height: "100%", display: "flex", overflow: "hidden", borderRadius: "6px" }}>
                                                            {found > 0 && <div style={{ flex: found, background: C.pink }} />}
                                                            {noRes > 0 && <div style={{ flex: noRes, background: C.orange }} />}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Segment>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 3 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                                    <Header as="h3" style={{ ...hRow, margin: 0 }}><Icon name="map" style={{ color: metricMeta.color, fontSize: "1em", margin: 0 }} /><span>Mapa dopytu a ponuky podľa kraja</span></Header>
                                    <div style={{ display: "inline-flex", background: "#f1f1f3", borderRadius: "20px", padding: "3px" }}>
                                        {MAP_METRICS.map((m) => {
                                            const active = mapMetric === m.key;
                                            return (
                                                <span
                                                    key={m.key}
                                                    onClick={() => setMapMetric(m.key)}
                                                    style={{
                                                        cursor: "pointer",
                                                        padding: "5px 14px",
                                                        borderRadius: "16px",
                                                        fontSize: "0.84rem",
                                                        fontWeight: "bold",
                                                        whiteSpace: "nowrap",
                                                        transition: "all 0.15s ease",
                                                        color: active ? "#fff" : "#888",
                                                        background: active ? m.color : "transparent",
                                                    }}
                                                >
                                                    {m.short}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                <Divider />
                                <Grid stackable>
                                    <Grid.Column width={10}>
                                        <RegionMap
                                            values={mapValues}
                                            selected={selectedRegion}
                                            onSelect={setSelectedRegion}
                                            valueLabel={metricMeta.short}
                                            toColor={metricMeta.color}
                                            formatValue={(v) => (v == null ? "—" : v)}
                                        />
                                    </Grid.Column>
                                    <Grid.Column width={6}>
                                        <Menu pointing secondary widths={2} style={{ marginBottom: "1.2rem" }}>
                                            <Menu.Item active={panelTab === "stats"} onClick={() => setPanelTab("stats")}>
                                                <Icon name="chart bar" /> Štatistiky kraja
                                            </Menu.Item>
                                            <Menu.Item active={panelTab === "localities"} onClick={() => setPanelTab("localities")}>
                                                <Icon name="building" /> Top mestá
                                            </Menu.Item>
                                        </Menu>

                                        {panelTab === "stats" && (selectedDetail ? (
                                            <div>
                                                <Header as="h4" style={{ marginBottom: "0.8rem" }}>
                                                    {selectedRegion}
                                                    <span
                                                        onClick={() => setSelectedRegion(null)}
                                                        style={{ float: "right", cursor: "pointer", color: C.grey, fontSize: "0.85rem", fontWeight: "normal" }}
                                                    >
                                                        <Icon name="close" /> zrušiť
                                                    </span>
                                                </Header>
                                                {detailRows.map((row) => (
                                                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.6rem 0", borderBottom: "1px solid #f1f1f3" }}>
                                                        <span style={{ color: "#555", fontSize: "0.95rem" }}>{row.label}</span>
                                                        <span style={{ fontWeight: "bold", color: row.color, fontSize: "1.2rem" }}>{row.value}</span>
                                                    </div>
                                                ))}

                                                {ratioInfo && (
                                                    <div style={{ marginTop: "1rem", background: ratioInfo.soft, border: `1px solid ${ratioInfo.color}33`, borderRadius: 10, padding: "0.9rem 1rem" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                            <span style={{ color: "#555", fontWeight: "bold" }}>Pomer dopyt / ponuka</span>
                                                            <span style={{ fontSize: "1.6rem", fontWeight: "bold", color: ratioInfo.color }}>
                                                                {ratioInfo.ratio == null ? "—" : ratioInfo.ratio}
                                                            </span>
                                                        </div>
                                                        {ratioInfo.ratio != null && (
                                                            <div style={{ ...legendHint, marginTop: 2 }}>
                                                                ≈ {Math.round(ratioInfo.ratio)} vyhľadávaní na 1 aktivitu
                                                            </div>
                                                        )}
                                                        <div style={{ display: "inline-block", marginTop: 8, background: ratioInfo.color, color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: "0.78rem", fontWeight: "bold" }}>
                                                            {ratioInfo.text}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ color: "#5c6370" }}>
                                                <p style={{ fontSize: "0.95rem", lineHeight: 1.5 }}><Icon name="hand pointer" /> Klikni na kraj v mape pre detail dopytu a ponuky.</p>
                                                <Header as="h5" style={{ color: "#666", textTransform: "uppercase", letterSpacing: "0.03em" }}>Poradie krajov — {metricMeta.short}</Header>
                                                {[...geoRegions]
                                                    .sort((a, b) => (Number(b[mapMetric]) || 0) - (Number(a[mapMetric]) || 0))
                                                    .slice(0, 8)
                                                    .map((r, idx) => (
                                                        <div key={r.region} style={{ display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.5rem 0", cursor: "pointer", borderBottom: "1px solid #f6f6f8" }} onClick={() => setSelectedRegion(r.region)}>
                                                            <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#f1f1f3", color: "#888", fontSize: "0.8rem", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{idx + 1}</span>
                                                            <span style={{ flex: 1, color: "#333" }}>{r.region.replace(" kraj", "")}</span>
                                                            <strong style={{ color: metricMeta.color, fontSize: "1.05rem" }}>{r[mapMetric] == null ? "—" : r[mapMetric]}</strong>
                                                        </div>
                                                    ))}
                                            </div>
                                        ))}

                                        {panelTab === "localities" && (() => {
                                            const limit = showAllCities ? citySorted.length : 5;
                                            const visible = citySorted.slice(0, limit);
                                            const maxCity = Math.max(1, ...citySorted.map((x) => Number(x[cityMetricKey]) || 0));
                                            const chips = CITY_METRICS.filter((m) => m.key !== cityMetricKey);
                                            return (
                                                <div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.6rem" }}>
                                                        <Header as="h5" style={{ color: "#666", margin: 0, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                                                            Mestá podľa: <span style={{ color: cityPrimary.color }}>{cityPrimary.label}</span>
                                                        </Header>
                                                        <span style={{ ...legendHint }}>{selectedRegion ? selectedRegion.replace(" kraj", "") : "všetky kraje"}</span>
                                                    </div>
                                                    {cityFallback && (
                                                        <div style={{ background: "#fff7e6", border: "1px solid #f2c98f", color: "#a86b00", borderRadius: 8, padding: "0.5rem 0.7rem", fontSize: "0.8rem", marginBottom: "0.7rem" }}>
                                                            <Icon name="info circle" /> Pomer dopyt/ponuka je len na úrovni kraja — mestá sú zoradené podľa dopytu.
                                                        </div>
                                                    )}
                                                    {visible.length === 0 && (
                                                        <EmptyState
                                                            icon="building outline"
                                                            text="Žiadne mestá"
                                                            hint={mapMetric === "supply_count"
                                                                ? "V tomto kraji nie sú aktívne aktivity s vyplnenou adresou."
                                                                : "Zatiaľ tu nie sú žiadne záznamy z vyhľadávania."}
                                                        />
                                                    )}
                                                    {visible.map((l, idx) => {
                                                        const primaryValue = Number(l[cityMetricKey]) || 0;
                                                        return (
                                                            <div key={`${l.region}-${l.city}`} style={{ display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.45rem 0", borderBottom: "1px solid #f4f4f6" }}>
                                                                <span style={{ width: 22, height: 22, borderRadius: "50%", background: cityPrimary.soft, color: cityPrimary.color, fontSize: "0.78rem", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{idx + 1}</span>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
                                                                        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                            <strong style={{ color: "#222" }}>{l.city}</strong>
                                                                            <span style={{ ...legendHint, marginLeft: 6 }}>{l.region.replace(" kraj", "")}</span>
                                                                        </span>
                                                                        <span style={{ flexShrink: 0, fontWeight: "bold", color: cityPrimary.color }}>{primaryValue}<span style={{ fontSize: "0.78rem", color: "#6b7280", fontWeight: "normal" }}> {cityPrimary.label.toLowerCase()}</span></span>
                                                                    </div>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: 3 }}>
                                                                        <div style={{ flex: 1, background: "#f1f1f3", borderRadius: 3, height: 5, overflow: "hidden" }}>
                                                                            <div style={{ width: `${Math.max((primaryValue / maxCity) * 100, 4)}%`, height: "100%", background: cityPrimary.color, borderRadius: 3 }} />
                                                                        </div>
                                                                        {chips.map((m) => (
                                                                            <span key={m.key} style={{ color: m.color, fontSize: "0.72rem", fontWeight: "bold", whiteSpace: "nowrap" }}>
                                                                                {m.label} {Number(l[m.key]) || 0}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {citySorted.length > 5 && (
                                                        <div style={{ marginTop: "0.7rem" }}>
                                                            <span onClick={() => setShowAllCities((prev) => !prev)} style={{ color: C.pink, cursor: "pointer", fontWeight: "bold", fontSize: "0.85rem" }}>
                                                                {showAllCities ? "Zobraziť menej" : `Zobraziť viac (${citySorted.length - 5})`}
                                                                <Icon name={showAllCities ? "chevron up" : "chevron down"} style={{ marginLeft: 4 }} />
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </Grid.Column>
                                </Grid>
                            </Segment>

                            <Segment style={{ ...sectionStyle, marginBottom: "1.5rem", order: 4 }}>
                                <Header as="h3" style={hRow}><Icon name="table" style={{ color: C.purple, fontSize: "1em", margin: 0 }} /><span>Tabuľka aktivít</span></Header>
                                <div style={{ ...sectionHint, margin: "0.35rem 0 0.9rem" }}>
                                    Aktivity s interakciou v zvolenom období, zoradené podľa zobrazení
                                </div>
                                <Table striped unstackable style={{ overflow: "auto" }}>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Aktivita</Table.HeaderCell>
                                            <Table.HeaderCell>Typ</Table.HeaderCell>
                                            <Table.HeaderCell>Program</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">Zobrazenia</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">Priem. čas (s)</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">Do košíka</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">Konverzia</Table.HeaderCell>
                                            <Table.HeaderCell textAlign="right">Zaplatené</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {activities.length === 0 && (
                                            <Table.Row>
                                                <Table.Cell colSpan={8} textAlign="center"><EmptyState padding="1rem 0" hint={null} /></Table.Cell>
                                            </Table.Row>
                                        )}
                                        {(showAllActivities ? activities : activities.slice(0, 8)).map((item) => {
                                            const modeColor = item.mode === "solidarity" ? C.blue : C.pink;
                                            const modeSoft = item.mode === "solidarity" ? C.blueSoft : C.pinkSoft;
                                            const viewsPct = maxTableViews > 0 ? Math.max((item.views / maxTableViews) * 100, 3) : 0;
                                            return (
                                                <Table.Row key={item.activity_id} verticalAlign="middle">
                                                    <Table.Cell>
                                                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: modeColor, marginRight: 8 }} />
                                                        <strong style={{ color: "#333" }}>{item.name}</strong>
                                                    </Table.Cell>
                                                    <Table.Cell style={{ color: "#777" }}>{item.type_name}</Table.Cell>
                                                    <Table.Cell>
                                                        <span style={{ background: modeSoft, color: modeColor, padding: "0.15rem 0.6rem", borderRadius: "12px", fontWeight: "bold", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                                                            {MODE_LABEL[item.mode] || item.mode}
                                                        </span>
                                                    </Table.Cell>
                                                    <Table.Cell style={{ minWidth: "120px" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                                                            <div style={{ flex: 1, background: "#f1f1f3", borderRadius: 4, height: 6, marginRight: 8, overflow: "hidden" }}>
                                                                <div style={{ width: `${viewsPct}%`, height: "100%", background: modeColor, borderRadius: 4 }} />
                                                            </div>
                                                            <strong style={{ color: "#333" }}>{item.views}</strong>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell textAlign="right" style={{ color: "#777" }}>{item.avg_time_seconds}s</Table.Cell>
                                                    <Table.Cell textAlign="right">
                                                        <span style={{ color: item.cart_adds > 0 ? C.green : "#bbb", fontWeight: "bold" }}>{item.cart_adds}</span>
                                                    </Table.Cell>
                                                    <Table.Cell textAlign="right">
                                                        <span style={{
                                                            background: item.conversion_rate >= 30 ? C.greenSoft : C.pinkSoft,
                                                            color: item.conversion_rate >= 30 ? C.green : C.pink,
                                                            padding: "0.2rem 0.6rem",
                                                            borderRadius: "12px",
                                                            fontWeight: "bold",
                                                        }}>
                                                            {item.conversion_rate}%
                                                        </span>
                                                    </Table.Cell>
                                                    <Table.Cell textAlign="right">
                                                        {item.paid_orders > 0 ? (
                                                            <span style={{ background: C.greenSoft, color: C.green, padding: "0.2rem 0.6rem", borderRadius: "12px", fontWeight: "bold" }}>
                                                                <Icon name="check" style={{ margin: 0 }} /> {item.paid_orders}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: "#ccc" }}>0</span>
                                                        )}
                                                    </Table.Cell>
                                                </Table.Row>
                                            );
                                        })}
                                    </Table.Body>
                                </Table>
                                {activities.length > 8 && (
                                    <div style={{ marginTop: "0.6rem" }}>
                                        <span onClick={() => setShowAllActivities((prev) => !prev)} style={{ color: C.pink, cursor: "pointer", fontWeight: "bold", fontSize: "0.88rem" }}>
                                            {showAllActivities ? "Zobraziť menej" : `Zobraziť viac (${activities.length - 8})`}
                                            <Icon name={showAllActivities ? "chevron up" : "chevron down"} style={{ marginLeft: 4 }} />
                                        </span>
                                    </div>
                                )}
                            </Segment>
                        </Segment>
                    </section>
                </Fragment>
            )}
        </>
    );
}

export default Analytics;
